import { Fragment, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  Save,
  CircleCheck,
  CircleX,
  TriangleAlert,
  Mail
} from 'lucide-react';
import {
  useApiAdminwaitingListsIdGet,
  useApiAdminwaitingListsIdPatch,
  useApiAdmintypologiesGetCollection,
  useApiCountriesGetCollection,
  useSubmitWaitingList
} from '@r/api/generated/client.ts';
import { WorkspaceConfigContext } from '@r/context/workspace-config-context.ts';
import { isTypologyConversion } from '@r/utils/workspace-utils.ts';
import {
  WaitingListWaitingListReadModelStatus,
  WaitingListWaitingListWriteModelRole
} from '@r/api/generated/client.schemas.ts';
import { defineStepper } from '@ui/stepper.tsx';
import { Button } from '@ui/button.tsx';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@ui/dialog.tsx';
import { Form } from '@ui/form.tsx';
import type { ApiCountry } from '@ui/country-dropdown.tsx';
import { Alert, AlertDescription, AlertTitle } from '@ui/alert.tsx';
import { Separator } from '@ui/separator.tsx';
import { Switch } from '@ui/switch.tsx';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@ui/tooltip.tsx';
import {
  buildWaitingListEditSchema,
  type WaitingListEditFormValues
} from '@admin/components/company/waiting-list/waiting-list-edit-form-schema.ts';
import {
  getAcceptMissingFields,
  isEmptyWaitingListPatch,
  waitingListEditCompanyStepFields,
  waitingListEditToPatch
} from '@admin/components/company/waiting-list/waiting-list-edit-modal-helpers.ts';
import {
  AcceptNoticeBlock,
  WaitingListEditCompanyFields,
  WaitingListEditManagerFields
} from '@admin/components/company/waiting-list/waiting-list-edit-modal-fields.tsx';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';

const {
  StepperProvider,
  StepperNavigation,
  StepperStep,
  StepperTitle,
  StepperPanel,
  StepperControls
} = defineStepper(
  { id: 'step-company', title: 'company' },
  { id: 'step-manager', title: 'manager' }
);

export function WaitingListEditModal({
  id,
  open,
  onOpenChange,
  onSuccess,
  canManageConversion
}: {
  id: number | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  canManageConversion: boolean;
}) {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();
  const queryClient = useQueryClient();
  const [confirmAction, setConfirmAction] = useState<'accept' | 'reject' | null>(null);
  const [emailNotification, setEmailNotification] = useState(true);
  const waitingListId = id ? String(id) : undefined;
  const workspaceConfig = useContext(WorkspaceConfigContext);
  const typologyConversionMode = isTypologyConversion(workspaceConfig);
  const schema = useMemo(
    () => buildWaitingListEditSchema(typologyConversionMode, t),
    [typologyConversionMode, t]
  );
  const form = useForm<WaitingListEditFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      companyName: '',
      companyUniqueCode: '',
      typology: '',
      tva: '',
      siren: '',
      addressLine1: '',
      addressLine2: '',
      zipcode: '',
      city: '',
      countryAlpha3: '',
      email: '',
      firstname: '',
      lastname: '',
      userUniqueCode: '',
      role: '',
      comment: '',
      pointCoeff: undefined
    },
    mode: 'onChange'
  });

  const waitingListQuery = useApiAdminwaitingListsIdGet(waitingListId, {
    query: { enabled: open && !!waitingListId }
  });
  const { data: countriesResponse, isLoading: isCountriesLoading } = useApiCountriesGetCollection();
  const countries = (countriesResponse?.data as ApiCountry[] | undefined) ?? [];

  const typologiesQuery = useApiAdmintypologiesGetCollection(undefined, {
    query: { enabled: open && typologyConversionMode }
  });
  const typologies = (typologiesQuery.data?.data ?? []) as Array<{
    id: number;
    name: string;
  }>;

  const typologyIriFromApi = useMemo(() => {
    const name = waitingListQuery.data?.typology?.name;
    if (!name) return '';
    const found = typologies.find((x) => x.name === name);
    return found ? `/admin/typologies/${found.id}` : '';
  }, [waitingListQuery.data?.typology?.name, typologies]);

  useEffect(() => {
    if (!waitingListQuery.data || !open) return;
    if (
      waitingListQuery.data.status !==
      WaitingListWaitingListReadModelStatus.waitingliststatuspending
    ) {
      toast.error(t('admin.waiting.list.edit.finalized'));
      onOpenChange(false);
      return;
    }
    form.reset(
      {
        companyName: waitingListQuery.data.companyName ?? '',
        companyUniqueCode: waitingListQuery.data.companyUniqueCode ?? '',
        typology: typologyIriFromApi,
        tva: waitingListQuery.data.tva ?? '',
        siren: waitingListQuery.data.siren ?? '',
        addressLine1: waitingListQuery.data.addressLine1 ?? '',
        addressLine2: waitingListQuery.data.addressLine2 ?? '',
        zipcode: waitingListQuery.data.zipcode ?? '',
        city: waitingListQuery.data.city ?? '',
        countryAlpha3: waitingListQuery.data.country?.alpha3 ?? '',
        email: waitingListQuery.data.email ?? '',
        firstname: waitingListQuery.data.firstname ?? '',
        lastname: waitingListQuery.data.lastname ?? '',
        userUniqueCode: waitingListQuery.data.userUniqueCode ?? '',
        role:
          waitingListQuery.data.role === WaitingListWaitingListWriteModelRole.ROLE_MEMBER ||
          waitingListQuery.data.role === WaitingListWaitingListWriteModelRole.ROLE_MANAGER
            ? waitingListQuery.data.role
            : '',
        comment: waitingListQuery.data.comment ?? '',
        pointCoeff:
          waitingListQuery.data.pointCoeff === null ||
          waitingListQuery.data.pointCoeff === undefined
            ? undefined
            : waitingListQuery.data.pointCoeff
      },
      { keepDirtyValues: true }
    );
  }, [form, open, waitingListQuery.data, typologyIriFromApi, t, onOpenChange]);

  const patchMutation = useApiAdminwaitingListsIdPatch({
    mutation: {
      onError: (error) => {
        showOrvalErrorToast(error, t('admin.waiting.list.edit.error'));
      }
    }
  });

  const submitMutation = useSubmitWaitingList({
    mutation: {
      onError: (error) => {
        showOrvalErrorToast(error, t('admin.waiting.list.edit.error'));
      }
    }
  });

  const patchIfDirty = useCallback(async () => {
    if (!waitingListId) return;
    const payload = waitingListEditToPatch(
      form.getValues(),
      form.formState.dirtyFields as Partial<Record<keyof WaitingListEditFormValues, boolean>>
    );
    if (isEmptyWaitingListPatch(payload)) {
      return;
    }
    await patchMutation.mutateAsync({ id: waitingListId, data: payload });
  }, [form, patchMutation, waitingListId]);

  const afterSuccess = useCallback(() => {
    toast.success(t('admin.waiting.list.edit.success'));
    queryClient.invalidateQueries({ queryKey: ['waiting_list_collection'] });
    onSuccess?.();
    onOpenChange(false);
  }, [onOpenChange, onSuccess, queryClient, t]);

  const roleOptions = [
    { value: WaitingListWaitingListWriteModelRole.ROLE_MEMBER, label: t('role.member') },
    { value: WaitingListWaitingListWriteModelRole.ROLE_MANAGER, label: t('role.manager') }
  ];
  const watchedValues = form.watch();
  const acceptMissingFields = useMemo(
    () => getAcceptMissingFields(form.getValues(), typologyConversionMode),
    [form, watchedValues, typologyConversionMode]
  );
  const isAcceptDisabled =
    !canManageConversion ||
    acceptMissingFields.length > 0 ||
    patchMutation.isPending ||
    submitMutation.isPending;

  const showConversionHints = canManageConversion && acceptMissingFields.length > 0;

  const savePending = useCallback(async () => {
    if (!waitingListId) return;
    const valid = await form.trigger();
    if (!valid) return;
    await patchIfDirty();
    afterSuccess();
  }, [afterSuccess, form, patchIfDirty, waitingListId]);

  const reject = useCallback(async () => {
    if (!canManageConversion || !waitingListId) return;
    const valid = await form.trigger();
    if (!valid) return;
    await patchIfDirty();
    await submitMutation.mutateAsync({
      id: waitingListId,
      data: { status: 'waiting.list.status.rejected' }
    });
    afterSuccess();
  }, [afterSuccess, canManageConversion, form, patchIfDirty, submitMutation, waitingListId]);

  const accept = useCallback(async () => {
    if (!canManageConversion || !waitingListId) return;
    const valid = await form.trigger();
    if (!valid) return;
    await patchIfDirty();
    await submitMutation.mutateAsync({
      id: waitingListId,
      data: { status: 'waiting.list.status.approved', emailNotification }
    });
    afterSuccess();
  }, [
    afterSuccess,
    canManageConversion,
    emailNotification,
    form,
    patchIfDirty,
    submitMutation,
    waitingListId
  ]);

  const handleStepNext = useCallback(
    async (methods: { current: { id: string }; next: () => void }) => {
      if (methods.current.id === 'step-company') {
        const ok = await form.trigger(waitingListEditCompanyStepFields);
        if (!ok) return;
        methods.next();
        return;
      }
      await savePending();
    },
    [form, savePending]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-10 max-w-3xl max-h-[90vh] overflow-y-auto scrollable">
        <DialogHeader>
          <DialogTitle>{t('admin.waiting.list.edit.title')}</DialogTitle>
          <DialogDescription>{t('admin.waiting.list.edit.description')}</DialogDescription>
        </DialogHeader>
        {waitingListQuery.isLoading || (open && !!waitingListId && !waitingListQuery.data) ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : (
          <Form {...form}>
            <StepperProvider variant="horizontal" labelOrientation="vertical" className="w-full">
              {({ methods }) => (
                <Fragment>
                  <StepperNavigation className="mb-8">
                    {methods.all.map((step) => (
                      <StepperStep key={step.id} of={step.id}>
                        <StepperTitle>
                          {step.id === 'step-company'
                            ? t('admin.waiting.list.step.company')
                            : t('admin.waiting.list.step.manager')}
                        </StepperTitle>
                      </StepperStep>
                    ))}
                  </StepperNavigation>
                  <div className="min-h-[280px] py-2">
                    {methods.switch({
                      'step-company': () => (
                        <StepperPanel>
                          <WaitingListEditCompanyFields
                            form={form}
                            countries={countries}
                            isCountriesLoading={isCountriesLoading}
                            typologyConversionMode={typologyConversionMode}
                            typologies={typologies}
                            typologiesLoading={typologiesQuery.isLoading}
                            showAcceptRequiredStars={showConversionHints}
                            t={t}
                          />
                        </StepperPanel>
                      ),
                      'step-manager': () => (
                        <StepperPanel>
                          <WaitingListEditManagerFields
                            form={form}
                            roleOptions={roleOptions}
                            typologyConversionMode={typologyConversionMode}
                            showAcceptRequiredStars={showConversionHints}
                            t={t}
                          />
                        </StepperPanel>
                      )
                    })}
                  </div>
                  <StepperControls className="flex flex-col gap-4 pt-6 w-full">
                    {methods.current.id === 'step-company' ? (
                      <>
                        <div className="flex flex-row flex-wrap gap-4 justify-between w-full">
                          <Button
                            type="button"
                            variant="outline"
                            rounded="full"
                            onClick={() => onOpenChange(false)}
                          >
                            {t('common.cancel')}
                          </Button>
                          <Button
                            type="button"
                            rounded="full"
                            disabled={patchMutation.isPending}
                            onClick={() => handleStepNext(methods)}
                            className="flex gap-2 items-center"
                          >
                            {t('common.next')}
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        </div>
                        {showConversionHints ? (
                          <AcceptNoticeBlock t={t} acceptMissingFields={acceptMissingFields} />
                        ) : null}
                      </>
                    ) : (
                      <>
                        <div className="flex flex-row flex-wrap gap-4 justify-between items-center w-full">
                          <Button
                            type="button"
                            variant="outline"
                            rounded="full"
                            onClick={() => methods.prev()}
                            className="flex gap-2 items-center"
                          >
                            <ChevronLeft className="w-4 h-4" />
                            {t('common.previous')}
                          </Button>
                          <Button
                            type="button"
                            rounded="full"
                            disabled={patchMutation.isPending || submitMutation.isPending}
                            onClick={savePending}
                            className="flex gap-2 items-center shrink-0"
                          >
                            {patchMutation.isPending || submitMutation.isPending ? (
                              <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                {t('common.sending')}
                              </>
                            ) : (
                              <>
                                <Save className="w-4 h-4" />
                                {t('form.save.modifications')}
                              </>
                            )}
                          </Button>
                        </div>
                        {canManageConversion ? (
                          <>
                            <Separator className="w-full" />
                            <div className="grid grid-cols-2 gap-2 w-full">
                              <Button
                                type="button"
                                variant="outline"
                                rounded="full"
                                disabled={patchMutation.isPending || submitMutation.isPending}
                                onClick={() => setConfirmAction('reject')}
                                className="flex gap-2 justify-center items-center w-full min-w-0"
                              >
                                <CircleX className="w-4 h-4 shrink-0" />
                                <span className="truncate">
                                  {t('admin.waiting.list.edit.cta.reject')}
                                </span>
                              </Button>
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span
                                      className={`flex min-w-0 w-full ${isAcceptDisabled ? 'cursor-not-allowed' : ''}`}
                                    >
                                      <Button
                                        type="button"
                                        rounded="full"
                                        disabled={isAcceptDisabled}
                                        onClick={() => setConfirmAction('accept')}
                                        className="flex gap-2 justify-center items-center w-full min-w-0 disabled:cursor-not-allowed"
                                      >
                                        <CircleCheck className="w-4 h-4 shrink-0" />
                                        <span className="truncate">
                                          {t('admin.waiting.list.edit.cta.accept')}
                                        </span>
                                      </Button>
                                    </span>
                                  </TooltipTrigger>
                                  {isAcceptDisabled ? (
                                    <TooltipContent>
                                      <p>
                                        {acceptMissingFields.length > 0
                                          ? t('admin.waiting.list.edit.cta.accept.disabled.hint')
                                          : t('common.sending')}
                                      </p>
                                    </TooltipContent>
                                  ) : null}
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                          </>
                        ) : null}
                        {showConversionHints ? (
                          <AcceptNoticeBlock t={t} acceptMissingFields={acceptMissingFields} />
                        ) : null}
                      </>
                    )}
                  </StepperControls>
                </Fragment>
              )}
            </StepperProvider>
          </Form>
        )}
        <Dialog
          open={canManageConversion && confirmAction !== null}
          onOpenChange={(openState) => !openState && setConfirmAction(null)}
        >
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle>{t('admin.waiting.list.edit.confirm.title')}</DialogTitle>
              <DialogDescription>
                {t('admin.waiting.list.edit.confirm.description')}
              </DialogDescription>
            </DialogHeader>
            <Alert variant="destructive">
              <TriangleAlert className="w-4 h-4" />
              <AlertTitle>{t('admin.waiting.list.edit.confirm.warning.title')}</AlertTitle>
              <AlertDescription>
                {t('admin.waiting.list.edit.confirm.warning.description')}
              </AlertDescription>
            </Alert>
            {confirmAction === 'accept' ? (
              <div className="flex items-center justify-between gap-4 rounded-md border p-3">
                <label
                  htmlFor="waiting-list-email-notification"
                  className="flex items-center gap-2 text-sm"
                >
                  <Mail className="w-4 h-4 shrink-0" />
                  {t('admin.waiting.list.edit.confirm.email.notification')}
                </label>
                <Switch
                  id="waiting-list-email-notification"
                  checked={emailNotification}
                  onCheckedChange={setEmailNotification}
                />
              </div>
            ) : null}
            <div className="flex gap-2 justify-end pt-2">
              <DialogClose asChild>
                <Button type="button" variant="outline">
                  {t('common.cancel')}
                </Button>
              </DialogClose>
              <Button
                type="button"
                onClick={async () => {
                  if (confirmAction === 'accept') await accept();
                  if (confirmAction === 'reject') await reject();
                  setConfirmAction(null);
                }}
                disabled={patchMutation.isPending || submitMutation.isPending}
              >
                {confirmAction === 'accept'
                  ? t('admin.waiting.list.edit.cta.accept')
                  : t('admin.waiting.list.edit.cta.reject')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}
